import type { FixtureElement } from "./types";

export type ProjectionCoverageState = "empty" | "none" | "partial" | "complete";

export interface ProjectionTypeCoverage {
  readonly type: string;
  readonly records: number;
  readonly renderable: number;
}

export interface ProjectionCoverage {
  readonly state: ProjectionCoverageState;
  readonly records: number;
  readonly planRecords: number;
  readonly renderable: number;
  readonly meshRenderable: number;
  readonly boxRenderable: number;
  readonly supportedGeometryRecords: number;
  readonly unrenderedPlanRecords: number;
  readonly producerSkippedRecords: number;
  readonly propertySetSkippedRecords: number;
  readonly unsupportedGeometrySkippedRecords: number;
  readonly eligibleGeometryRecords: number;
  readonly missingGeometryRecords: number;
  readonly unsupportedGeometryRecords: number;
  readonly nonGeometricRetainedRecords: number;
  readonly unclassifiedRetainedRecords: number;
  readonly overallRatio: number;
  readonly planRatio: number;
  readonly ratio: number;
  readonly types: readonly ProjectionTypeCoverage[];
  readonly skippedTypes: readonly { readonly value: string; readonly count: number }[];
  readonly skippedReasons: readonly { readonly value: string; readonly count: number }[];
}

export interface ThreeWayAccounting {
  readonly renderable: number;
  readonly unrenderedPlanEvidence: number;
  readonly producerSkipped: number;
}

/**
 * Exactly-once renderable／unrendered-plan-evidence／producer-skipped split for an arbitrary element
 * subset (e.g. a Report group). Reuses the same predicate `summarizeProjectionCoverage` applies at
 * whole-model scope, so a Report group is never given a different definition of these three states.
 */
export function threeWayAccounting(elements: readonly FixtureElement[]): ThreeWayAccounting {
  let renderable = 0;
  let producerSkipped = 0;
  for (const element of elements) {
    if (element.type === "Skipped") {
      producerSkipped += 1;
      continue;
    }
    if (element.geometry !== null) renderable += 1;
  }
  return { renderable, producerSkipped, unrenderedPlanEvidence: elements.length - renderable - producerSkipped };
}

type GeometryEligibility = "rendered" | "missing" | "unsupported" | "non-geometric" | "unclassified";

function geometryEligibility(element: FixtureElement): GeometryEligibility {
  if (element.geometry !== null) return "rendered";
  if (element.type === "Wall") return "missing";
  if (element.type === "Skipped") {
    if (element.properties.Reason === "unsupported_geometry") return "unsupported";
    if (element.properties.IfcType === "IFCPROPERTYSET") return "non-geometric";
    return "unclassified";
  }
  if (element.type === "Building" || element.type === "Floor") return "non-geometric";
  return "unclassified";
}

export function summarizeProjectionCoverage(elements: readonly FixtureElement[]): ProjectionCoverage {
  const typeCounts = new Map<string, { records: number; renderable: number }>();
  const skippedTypes = new Map<string, number>();
  const skippedReasons = new Map<string, number>();
  let renderable = 0;
  let meshRenderable = 0;
  let boxRenderable = 0;
  let producerSkippedRecords = 0;
  let unrenderedPlanRecordsCount = 0;
  const eligibilityCounts: Record<GeometryEligibility, number> = {
    rendered: 0,
    missing: 0,
    unsupported: 0,
    "non-geometric": 0,
    unclassified: 0
  };
  for (const element of elements) {
    eligibilityCounts[geometryEligibility(element)] += 1;
    // A "Skipped" record's own type/reason census (producerSkippedRecords, skippedTypes,
    // skippedReasons) answers "how many entities did the plan importer fail to reconstruct at
    // all" -- that stays true regardless of geometry, so every Skipped record is still counted
    // here unconditionally. That is a different question from "is this record renderable now":
    // ifc-mesh-import.ts's mergeMeshGeometryIntoFixture can give a Skipped record real mesh
    // geometry when the separately-pinned mesh engine succeeded where the plan importer did not,
    // so an early `continue` here (this loop's previous shape) silently dropped every such record
    // from `renderable`/`meshRenderable` even after it had real geometry to show.
    if (element.type === "Skipped") {
      producerSkippedRecords += 1;
      const ifcType = typeof element.properties.IfcType === "string" ? element.properties.IfcType : "unknown";
      const reason = typeof element.properties.Reason === "string" ? element.properties.Reason : "unknown";
      skippedTypes.set(ifcType, (skippedTypes.get(ifcType) ?? 0) + 1);
      skippedReasons.set(reason, (skippedReasons.get(reason) ?? 0) + 1);
    } else {
      const counts = typeCounts.get(element.type) ?? { records: 0, renderable: 0 };
      counts.records += 1;
      if (element.geometry !== null) counts.renderable += 1;
      else unrenderedPlanRecordsCount += 1;
      typeCounts.set(element.type, counts);
    }
    if (element.geometry !== null) {
      renderable += 1;
      // Renderable species split (which drives 3D view coverage: shown-via-mesh vs.
      // shown-via-fallback-box) is exactly-once because `.geometry.kind` is a single field: every
      // renderable record is either mesh or box, never both, never neither.
      if (element.geometry.kind === "mesh") meshRenderable += 1;
      else boxRenderable += 1;
    }
  }
  const records = elements.length;
  const planRecords = records - producerSkippedRecords;
  const supportedTypes = new Set(
    [...typeCounts].filter(([, counts]) => counts.renderable > 0).map(([type]) => type)
  );
  const supportedGeometryRecords = [...typeCounts]
    .filter(([type]) => supportedTypes.has(type))
    .reduce((count, [, counts]) => count + counts.records, 0);
  // Not `planRecords - renderable`: a mesh-matched "Skipped" record (see the loop above) is
  // renderable without being plan-scoped, so that subtraction could go negative once mesh
  // geometry starts covering more than the plan importer ever put in scope. This direct count
  // only ever includes a genuinely plan-scoped (non-Skipped), still-geometry-less record.
  const unrenderedPlanRecords = unrenderedPlanRecordsCount;
  const state: ProjectionCoverageState =
    records === 0 ? "empty" : renderable === 0 ? "none" : unrenderedPlanRecords === 0 ? "complete" : "partial";
  const sortedCensus = (census: ReadonlyMap<string, number>): readonly { readonly value: string; readonly count: number }[] =>
    [...census].map(([value, count]) => ({ value, count })).sort((left, right) => right.count - left.count || left.value.localeCompare(right.value));
  return {
    state,
    records,
    planRecords,
    renderable,
    meshRenderable,
    boxRenderable,
    supportedGeometryRecords,
    unrenderedPlanRecords,
    producerSkippedRecords,
    propertySetSkippedRecords: skippedTypes.get("IFCPROPERTYSET") ?? 0,
    unsupportedGeometrySkippedRecords: skippedReasons.get("unsupported_geometry") ?? 0,
    eligibleGeometryRecords: eligibilityCounts.rendered + eligibilityCounts.missing + eligibilityCounts.unsupported,
    missingGeometryRecords: eligibilityCounts.missing,
    unsupportedGeometryRecords: eligibilityCounts.unsupported,
    nonGeometricRetainedRecords: eligibilityCounts["non-geometric"],
    unclassifiedRetainedRecords: eligibilityCounts.unclassified,
    overallRatio: records === 0 ? 0 : renderable / records,
    planRatio: planRecords === 0 ? 0 : renderable / planRecords,
    ratio: supportedGeometryRecords === 0 ? 0 : renderable / supportedGeometryRecords,
    types: [...typeCounts.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([type, counts]) => ({ type, ...counts })),
    skippedTypes: sortedCensus(skippedTypes),
    skippedReasons: sortedCensus(skippedReasons)
  };
}
